import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Plx from 'react-plx';
import MapViewer from './MapViewer';
import { randomValueBetween } from 'helpers/number';
import CountyDetails from './CountyDetails';
import useWindowPositioning from 'hooks/useWindowPositioning';
import { formatCurrency } from 'helpers/number';
import MSLALogo from 'components/MSLALogo';
import './index.css';

const cardSides = {
  position: 'absolute',
  width: '100%',
  minHeight: '100%',
  '-webkit-backface-visibility': 'hidden',
  backfaceVisibility: 'hidden',
  backgroundColor: '#fff',
  color: 'black',
  borderRadius: '5px',
};

const useStyles = makeStyles((theme) => ({
  heroTitle: {
    fontFamily: 'Roboto Slab',
    fontWeight: '700',
    color: '#fff',
    margin: '1.5rem',
    userSelect: 'none',
  },
  mslaLogo: {
    transform: 'rotate(4deg)',
  },
  mapPanel: {
    userSelect: 'none',
  },
  detailsPanel: {
    userSelect: 'none',
  },
  flipCard: {
    backgroundColor: 'transparent',
    height: '320px',
    maxWidth: '500px',
    perspective: '1000px',
    userSelect: 'none',
    '&.show-back .flip-card-inner': {
      transform: 'rotateY(180deg)',
    },
  },
  flipCardInner: {
    position: 'relative',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    transition: 'transform 0.6s',
    transformStyle: 'preserve-3d',
    boxShadow: '0 4px 8px 4px rgba(0,0,0,0.2)',
  },
  flipCardFront: {
    ...cardSides,
  },
  flipCardBack: {
    ...cardSides,
    transform: 'rotateY(180deg)',
  },
}));

const getCountyNames = (counties = []) => {
  return counties.map((county) => county.countyName);
};

const createCountyDetailsMap = (counties = []) => {
  return counties && counties.length
    ? counties.reduce((acc, county) => {
        const { countyName } = county;
        acc[countyName] = { ...county };
        return acc;
      }, {})
    : [];
};

const HeroCountyHeatMap = ({
  panelSize = '600px',
  totalPremiumCollected = 0,
  quarter,
  year,
  data = [],
}) => {
  const { windowDimensions, elementRef } = useWindowPositioning();
  const classes = useStyles();
  const autoplayHandle = React.useRef(null);
  const [autoplay, setAutoplay] = React.useState(true);
  const [selectedCounty, setSelectedCounty] = React.useState(null);
  const [idleHighlightCounties, setIdleHighlightCounties] = React.useState([
    'Monroe',
    'Yazoo',
    'Scott',
    'Madison',
    'Greene',
    'Attala',
    'Winston',
    'Warren',
    'Tunica',
    'Chickasaw',
    'Washington',
  ]);
  const [countyDetailsMap, setCountyDetailsMap] = React.useState({});
  const [showBack, setShowBack] = React.useState(false);
  const [cardBackContent, setCardBackContent] = React.useState({});
  const [cardFrontContent, setCardFrontContent] = React.useState({ name: 'Monroe' });

  React.useEffect(() => {
    if (data.length) {
      const countyNames = getCountyNames(data);
      setIdleHighlightCounties(countyNames);
      setCountyDetailsMap(createCountyDetailsMap(data));
      const startCountyIndex = randomValueBetween(0, data.length - 1);
      const startCounty = {
        name: data[startCountyIndex].countyName,
        details: { ...data[startCountyIndex] },
      };
      setCardFrontContent(startCounty);

      setSelectedCounty(startCounty);
    }
  }, [data]);

  React.useEffect(() => {
    const clear = () => {
      if (autoplayHandle.current) {
        clearTimeout(autoplayHandle.current);
      }
    };

    clear();

    const playOnce = () => {
      clear();
      autoplayHandle.current = setTimeout(() => {
        const randomCounty =
          idleHighlightCounties[randomValueBetween(0, idleHighlightCounties.length - 1)];
        setSelectedCounty({ name: randomCounty, details: countyDetailsMap[randomCounty] });
        if (showBack) {
          setCardFrontContent({ name: randomCounty, details: countyDetailsMap[randomCounty] });
        } else {
          setCardBackContent({ name: randomCounty, details: countyDetailsMap[randomCounty] });
        }

        setTimeout(() => {
          setShowBack((showBack) => !showBack);
        }, 200);
        playOnce();
      }, 5000);
    };

    if (autoplay) {
      playOnce();
    }

    return () => {
      clear();
    };
  }, [idleHighlightCounties, autoplay, showBack, countyDetailsMap]);

  const handleCountySelect = React.useCallback(
    (county) => {
      setSelectedCounty(county);

      setAutoplay(false);
      if (!showBack) {
        setCardFrontContent({ ...county, details: countyDetailsMap[county.name] });
      } else {
        setCardBackContent({ ...county, details: countyDetailsMap[county.name] });
      }
      setTimeout(() => {
        setShowBack((showBack) => !showBack);
      }, 200);
    },
    [showBack, countyDetailsMap],
  );

  return (
    <>
      <div
        ref={elementRef}
        data-testid="hero-county_root"
        style={{
          height: panelSize,
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(36deg, rgba(1, 31, 70, 1) 0%, rgba(5, 120, 230, 1) 100%)',
        }}
      >
        {/* Background Logo */}
        <Plx
          data-testid="hero-county_headline-panel-logo"
          className={classes.headline}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd + 100,
              easing: 'ease-in',
              properties: [
                {
                  startValue: 10,
                  endValue: 0,
                  property: 'translateY',
                  unit: '%',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart - 100,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 0,
                  endValue: -10,
                  property: 'translateY',
                  unit: '%',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            top: -150,
            width: '100%',
            zIndex: 100,
            opacity: 0.05,
            textAlign: 'center',
          }}
        >
          <MSLALogo className={classes.mslaLogo} />
        </Plx>
        {/* Headline */}
        <Plx
          data-testid="hero-county_headline-panel"
          className={classes.headline}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd,
              easing: 'ease-in',
              properties: [
                {
                  startValue: 10,
                  endValue: 0,
                  property: 'translateX',
                  unit: '%',
                },
                {
                  startValue: 0,
                  endValue: 1,
                  property: 'opacity',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart - 200,
              end: windowDimensions.animationExitEnd - 200,
              properties: [
                {
                  startValue: 1,
                  endValue: 0,
                  property: 'opacity',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            top: 0,
            width: '100%',
            height: '80px',
            zIndex: 100,
            transform: 'translateX(10)',
            opacity: 0,
          }}
        >
          <div className={`${classes.heroTitle} county-hero-title`}>
            {formatCurrency(totalPremiumCollected)} premium was collected in Q{quarter} {year}
          </div>
        </Plx>
        {/* Details Panel */}
        <Plx
          data-testid="hero-county_details-view-panel"
          className={classes.detailsPanel}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd + 100,
              easing: 'ease-in',
              properties: [
                {
                  startValue: 30,
                  endValue: 0,
                  property: 'translateY',
                  unit: '%',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart - 100,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 0,
                  endValue: -30,
                  property: 'translateY',
                  unit: '%',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            top: 0,
            width: '100%',
            height: panelSize,
            zIndex: 150,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ flex: 1, display: 'flex', width: '100%' }}>
              <div
                style={{
                  flex: 1,
                  flexDirection: 'column',
                  height: '100%',
                }}
                className="panel-spacer"
              ></div>
              {selectedCounty ? (
                <div
                  style={{
                    flex: 1,
                    paddingTop: '10rem',
                    paddingRight: '1rem',
                    textAlign: 'center',
                  }}
                >
                  <div
                    className={`${classes.flipCard} ${showBack ? 'show-back' : ''} flip-card`}
                    data-testid="hero-county_flip-card"
                  >
                    <div className={`${classes.flipCardInner} flip-card-inner`}>
                      <div className={`${classes.flipCardFront}`}>
                        <CountyDetails county={cardFrontContent} mapId="CountyFromState1" />
                      </div>
                      <div className={`${classes.flipCardBack}`}>
                        <CountyDetails county={cardBackContent} mapId="CountyFromState2" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </Plx>
        {/* Map Panel */}
        <Plx
          data-testid="hero-county_map-panel"
          className={classes.mapPanel}
          parallaxData={[
            {
              start: windowDimensions.animationStart,
              end: windowDimensions.animationEnd + 100,
              easing: 'ease-in',
              properties: [
                {
                  startValue: 60,
                  endValue: 0,
                  property: 'translateY',
                  unit: '%',
                },
              ],
            },
            {
              start: windowDimensions.animationExitStart - 100,
              end: windowDimensions.animationExitEnd,
              properties: [
                {
                  startValue: 0,
                  endValue: -60,
                  property: 'translateY',
                  unit: '%',
                },
              ],
            },
          ]}
          style={{
            position: 'absolute',
            top: 0,
            width: '50%',
            height: panelSize,
            zIndex: 200,
            transformOrigin: '40% 40%',
          }}
        >
          <div
            data-testid="hero-county_map-container"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              paddingTop: '1.5rem',
            }}
          >
            {selectedCounty ? (
              <MapViewer onCountySelect={handleCountySelect} selectedCounty={selectedCounty} />
            ) : null}
          </div>
        </Plx>
      </div>
    </>
  );
};

export default HeroCountyHeatMap;
